import { t } from "@lingui/macro";
import { ReactElement } from "react";
import React from "react";
import {
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useKeyboardBottomInset } from "../../../hooks/useKeyboardBottomInset";
import { WhooshThemeColors } from "../../../theme/restyle/theme";
import { Button } from "../../controls/Button/Button";
import { CloseIcon } from "../../icons_v2/general";
import { Box, HStack, Text, VStack } from "../../primitive";
import { Modal } from "../Modal/Modal";

export type StandardModalProps = {
  /**
   * Renders at the top of the modal.
   */
  upperTitle?: string;
  /*
   * Renders in the upper right corner of the modal
   */
  upperNode?: ReactElement;
  /**
   * The modal will handle making this scrollable if too tall
   */
  content?: ReactElement;
  /**
   * Will pin to the bottom of the modal
   */
  footerContent?: ReactElement | null;
  isOpen: boolean;
  onClose?: () => void;
  hideCloseButton?: boolean;
  CloseButtonIcon?: ReactElement;
  lockFullHeight?: boolean;
  disableContentScroll?: boolean;
  subtitle?: string;
  /**
   * Allows you to change the color of the background
   */
  backgroundColorOverride?: WhooshThemeColors;
  hideFooterPadding?: boolean;
  /** Removes the header inset when rendering a custom full-width upperNode. */
  hideHeaderPadding?: boolean;
  allowShrink?: boolean;
};

export const StandardModal = ({
  upperTitle,
  upperNode,
  content,
  isOpen,
  onClose,
  footerContent,
  hideCloseButton,
  CloseButtonIcon = <CloseIcon />,
  lockFullHeight,
  disableContentScroll = false,
  subtitle,
  backgroundColorOverride,
  hideFooterPadding = false,
  hideHeaderPadding = false,
  allowShrink = false,
}: StandardModalProps) => {
  const windowHeight = Dimensions.get("window").height;

  const insets = useSafeAreaInsets();
  const keyboardInset = useKeyboardBottomInset();
  const isKeyboardVisible = Platform.OS === "ios" && keyboardInset > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <StatusBar
        animated={true}
        barStyle="dark-content"
        backgroundColor="white"
      />
      <Box
        backgroundColor={
          backgroundColorOverride ? backgroundColorOverride : "surface-primary"
        }
        overflow={"hidden"}
        borderRadius={{ base: "0", md: "6" }}
        height={{ base: "100%", md: lockFullHeight ? "100%" : "auto" }}
        width={{ base: "100%", md: 400 }}
        maxWidth={{ base: "100%", md: 400 }}
        maxHeight={{ base: "100%", md: windowHeight - 200 }}
        minHeight={{ base: "100%", md: allowShrink ? 0 : 600 }}
      >
        <KeyboardAvoidingView
          style={{ width: "100%", height: "100%" }}
          behavior="padding"
        >
          <VStack
            height="100%"
            flex={1}
            style={{
              paddingTop: insets.top,
              paddingBottom: isKeyboardVisible ? 0 : insets.bottom,
            }}
          >
            {hideCloseButton && !upperNode ? null : (
              <HStack
                mx={hideHeaderPadding ? 0 : 6}
                mt={hideHeaderPadding ? 0 : 6}
                justifyContent={
                  hideCloseButton
                    ? hideHeaderPadding
                      ? "flex-start"
                      : "flex-end"
                    : "space-between"
                }
                alignItems="center"
              >
                {hideCloseButton ? null : (
                  <Button
                    size="regular"
                    accessibilityLabel={t`Modal Close Button`}
                    testID="modal-close-btn"
                    variant="secondary"
                    onPress={() => {
                      onClose?.();
                    }}
                    leftIcon={CloseButtonIcon}
                  />
                )}
                {hideHeaderPadding && hideCloseButton && upperNode ? (
                  <Box flexGrow={1} minWidth={0} width="100%">
                    {upperNode}
                  </Box>
                ) : (
                  upperNode
                )}
              </HStack>
            )}
            {upperTitle ? (
              <VStack pt={8} pb={6} mx={6}>
                {subtitle ? (
                  <Text color="text-secondary" variant="mainSmallMedium" mb={1}>
                    {subtitle}
                  </Text>
                ) : null}

                <Text variant="mainTitle2">{upperTitle}</Text>
              </VStack>
            ) : null}
            {disableContentScroll ? (
              <Box
                flex={1}
                position="relative"
                style={{ paddingBottom: footerContent ? 140 : 0 }}
              >
                {content}
              </Box>
            ) : (
              <ScrollView
                contentContainerStyle={{
                  paddingBottom: !!footerContent ? 140 : 0,
                }}
                style={{ flex: 1 }}
              >
                {content}
              </ScrollView>
            )}
            {footerContent ? (
              <Box paddingHorizontal={hideFooterPadding ? 0 : 6}>
                {footerContent}
              </Box>
            ) : null}
          </VStack>
        </KeyboardAvoidingView>
      </Box>
    </Modal>
  );
};
